import React, { useState, useEffect } from "react";
import './App.css';
import { FaCog, FaBell, FaTrash, FaPlus, FaUpload, FaDownload, FaArrowLeft } from 'react-icons/fa';

const API_URL = (process.env.REACT_APP_API_URL || 'https://api.sentiguard.ai') + '/alarms';

const TIPURI = [
  { value: "microfon", label: "Microphone dB", unit: "dB" },
  { value: "baterie", label: "Battery %", unit: "%" },
  { value: "gsm", label: "GSM dBm", unit: "dBm" },
  { value: "senzor_proximitate", label: "Proximity sensor", unit: "cm" },
  { value: "senzor_miscare", label: "Motion sensor", unit: "value" },
  { value: "accel_rms", label: "Acceleration √(x²+y²+z²)", unit: "m/s²" }
];
const OPERATORI = [
  { value: ">", label: "greater than" },
  { value: "<", label: "less than" },
  { value: "=", label: "equal to" }
];
const TIP_ALERTA = [
  { value: 'completa', label: 'Full' },
  { value: 'soft', label: 'Soft' },
  { value: 'intarziata', label: 'Delayed' },
  { value: 'ignorare', label: 'Ignore' }
];

const TIPURI_LABEL = {
  microfon: "Microphone dB",
  baterie: "Battery %",
  gsm: "GSM dBm",
  senzor_proximitate: "Proximity sensor",
  senzor_miscare: "Motion sensor",
  accel_rms: "Acceleration √(x²+y²+z²)"
};
const TIPURI_UNIT = {
  microfon: "dB",
  baterie: "%",
  gsm: "dBm",
  senzor_proximitate: "cm",
  senzor_miscare: "",
  accel_rms: "m/s²"
};
const OP_LABEL = {
  ">": ">",
  "<": "<",
  "=": "="
};

const ALERT_COLOR = {
  completa: '#ef4444',
  soft: '#f59e0b',
  intarziata: '#3b82f6',
  ignorare: '#94a3b8'
};

function formatConditie(cond) {
  if (!cond || typeof cond !== 'object') return '';
  return `${TIPURI_LABEL[cond.tip] || cond.tip} ${OP_LABEL[cond.operator] || cond.operator} ${cond.valoare}${TIPURI_UNIT[cond.tip] ? ' ' + TIPURI_UNIT[cond.tip] : ''}`;
}

function formatDeclansatori(json) {
  try {
    const arr = typeof json === 'string' ? JSON.parse(json) : json;
    if (Array.isArray(arr)) {
      return arr.map(formatConditie).join(' AND ');
    }
    return '';
  } catch {
    return '';
  }
}

export default function AlarmSettings({ onLogout }) {
  const [alarms, setAlarms] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ scenariu: "", description: "", conditii: [], timp: { value: '', unit: 'seconds' }, tip: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [lastUploadedFile, setLastUploadedFile] = useState(null);

  useEffect(() => { fetchAlarms(); }, []);

  const fetchAlarms = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(API_URL);
      if (!res.ok) throw new Error("Error loading alarms");
      const data = await res.json();
      setAlarms(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const adaugaConditie = () => {
    setForm({ ...form, conditii: [...form.conditii, { tip: "microfon", operator: ">", valoare: "" }] });
  };

  const modificaConditie = (idx, field, value) => {
    const newConditii = [...form.conditii];
    newConditii[idx][field] = value;
    setForm({ ...form, conditii: newConditii });
  };

  const stergeConditie = (idx) => {
    setForm({ ...form, conditii: form.conditii.filter((_, i) => i !== idx) });
  };

  const handleAdd = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch((process.env.REACT_APP_API_URL || 'https://api.sentiguard.ai') + '/alarms', {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({
          scenariu: form.scenariu,
          description: form.description,
          declansatori: JSON.stringify(form.conditii),
          timp: form.timp.value || '0',
          tip: form.tip
        })
      });
      if (!res.ok) throw new Error("Error saving alarm");
      setForm({ scenariu: "", description: "", conditii: [], timp: { value: '', unit: 'seconds' }, tip: "" });
      setShowForm(false);
      fetchAlarms();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this alarm?")) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Error deleting alarm");
      fetchAlarms();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const allowedTypes = ['.txt', '.bin', '.ver'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!allowedTypes.includes(fileExtension)) {
      setUploadError("Only .txt, .bin and .ver files are allowed!");
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      setUploadError("File is too large! Maximum size is 50MB.");
      return;
    }
    setUploadLoading(true);
    setUploadError("");
    setUploadSuccess("");
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadUrl = (process.env.REACT_APP_API_URL || 'https://api.sentiguard.ai') + '/simple-file-upload';
      const response = await fetch(uploadUrl, { method: 'POST', body: formData });
      const result = await response.json();
      if (result.success) {
        setUploadSuccess(`File ${file.name} uploaded successfully!`);
        setLastUploadedFile({
          fileName: result.data.file_name,
          originalName: result.data.original_name,
          downloadUrl: result.data.download_url,
          fileSize: result.data.file_size
        });
        event.target.value = '';
      } else {
        setUploadError(result.message || "Error uploading file");
      }
    } catch {
      setUploadError("Connection error to server");
    } finally {
      setUploadLoading(false);
    }
  };

  return (
    <div className="settings-page">
      {/* Header */}
      <header className="settings-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <a
            href="/"
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '6px 14px',
              background: 'rgba(255,255,255,.1)',
              border: '1px solid rgba(255,255,255,.15)',
              borderRadius: 8,
              color: '#94a3b8',
              textDecoration: 'none',
              fontSize: 13,
              fontWeight: 600,
              transition: 'all .2s',
            }}
            onMouseOver={e => { e.currentTarget.style.background = 'rgba(255,255,255,.18)'; e.currentTarget.style.color = '#fff'; }}
            onMouseOut={e => { e.currentTarget.style.background = 'rgba(255,255,255,.1)'; e.currentTarget.style.color = '#94a3b8'; }}
          >
            <FaArrowLeft style={{ fontSize: 12 }} /> Dashboard
          </a>
          <span style={{ color: 'rgba(255,255,255,.2)', fontSize: 18 }}>|</span>
          <span className="settings-title">
            <FaCog style={{ fontSize: 16, marginRight: 8 }} />
            Alarm Settings
          </span>
        </div>
        <div className="settings-nav">
          <button
            onClick={onLogout}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '6px 14px',
              background: 'rgba(239,68,68,.15)',
              border: '1px solid rgba(239,68,68,.3)',
              borderRadius: 8,
              color: '#fca5a5',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all .2s',
            }}
            onMouseOver={e => { e.currentTarget.style.background = 'rgba(239,68,68,.3)'; e.currentTarget.style.color = '#fff'; }}
            onMouseOut={e => { e.currentTarget.style.background = 'rgba(239,68,68,.15)'; e.currentTarget.style.color = '#fca5a5'; }}
          >
            Logout
          </button>
        </div>
      </header>

      <div className="settings-content">
        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
          <button
            className="btn btn-success btn-md"
            onClick={() => setShowForm(true)}
          >
            <FaPlus /> New Alarm Scenario
          </button>

          <label
            className="btn btn-primary btn-md"
            style={{ cursor: 'pointer' }}
          >
            <FaUpload /> {uploadLoading ? 'Uploading...' : 'Upload File System'}
            <input
              type="file"
              accept=".txt,.bin,.ver"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
              disabled={uploadLoading}
            />
          </label>

          {lastUploadedFile && (
            <a
              href={lastUploadedFile.downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-ghost btn-md"
            >
              <FaDownload /> {lastUploadedFile.originalName} ({(lastUploadedFile.fileSize / 1024).toFixed(1)} KB)
            </a>
          )}
        </div>

        {/* Status messages */}
        {uploadSuccess && (
          <div style={{ padding: '10px 16px', marginBottom: 16, background: '#d1fae5', border: '1px solid #6ee7b7', borderLeft: '4px solid #10b981', borderRadius: 8, color: '#065f46', fontSize: 14, fontWeight: 600 }}>
            ✓ {uploadSuccess}
          </div>
        )}
        {uploadError && (
          <div style={{ padding: '10px 16px', marginBottom: 16, background: '#fee2e2', border: '1px solid #fca5a5', borderLeft: '4px solid #ef4444', borderRadius: 8, color: '#991b1b', fontSize: 14, fontWeight: 600 }}>
            ✕ {uploadError}
          </div>
        )}
        {error && (
          <div style={{ padding: '10px 16px', marginBottom: 16, background: '#fee2e2', border: '1px solid #fca5a5', borderLeft: '4px solid #ef4444', borderRadius: 8, color: '#991b1b', fontSize: 14, fontWeight: 600 }}>
            ✕ {error}
          </div>
        )}
        {loading && (
          <div style={{ padding: '10px 16px', marginBottom: 16, background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 8, color: '#1e40af', fontSize: 14 }}>
            Loading...
          </div>
        )}

        {/* Table */}
        <div className="settings-table-wrap">
          {alarms.length === 0 && !loading ? (
            <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8' }}>
              <FaBell style={{ fontSize: 40, marginBottom: 12, opacity: .4 }} />
              <div style={{ fontSize: 16, fontWeight: 600 }}>No alarm scenarios yet</div>
              <div style={{ fontSize: 13, marginTop: 4 }}>Click "New Alarm Scenario" to create one.</div>
            </div>
          ) : (
            <table className="settings-table">
              <thead>
                <tr>
                  <th>Scenario</th>
                  <th>Description</th>
                  <th>Triggers</th>
                  <th>Confirmation time</th>
                  <th>Alert type</th>
                  <th style={{ textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {alarms.map((a, idx) => (
                  <tr key={a.id || idx}>
                    <td style={{ fontWeight: 600, color: '#1e293b' }}>{a.scenariu}</td>
                    <td style={{ color: '#475569', fontSize: 13 }}>{a.description || <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                    <td style={{ color: '#475569', fontSize: 13 }}>{formatDeclansatori(a.declansatori)}</td>
                    <td style={{ color: '#475569', fontSize: 13 }}>{a.timp}s</td>
                    <td>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                        padding: '3px 10px',
                        borderRadius: 9999,
                        fontSize: 12,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '.4px',
                        background: (ALERT_COLOR[a.tip] || '#94a3b8') + '22',
                        color: ALERT_COLOR[a.tip] || '#64748b',
                        border: `1px solid ${(ALERT_COLOR[a.tip] || '#94a3b8')}44`
                      }}>
                        {a.tip || '-'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        onClick={() => handleDelete(a.id)}
                        className="btn btn-danger btn-sm"
                        title="Delete alarm"
                      >
                        <FaTrash style={{ fontSize: 11 }} /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">
              <FaBell style={{ color: '#ef4444', fontSize: 20 }} />
              New Alarm Scenario
            </h3>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>Scenario name</label>
              <input
                className="modal-input"
                placeholder="e.g. High temperature alert"
                value={form.scenariu}
                onChange={e => setForm({ ...form, scenariu: e.target.value })}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>Description</label>
              <input
                className="modal-input"
                placeholder="e.g. Triggered when microphone level exceeds threshold"
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
              />
            </div>

            {/* Trigger conditions */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.5px' }}>Trigger conditions</label>
                <button type="button" onClick={adaugaConditie} className="btn btn-success btn-sm">
                  <FaPlus style={{ fontSize: 10 }} /> Add
                </button>
              </div>
              {form.conditii.length === 0 && (
                <div style={{ padding: '12px 16px', background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 8, color: '#94a3b8', fontSize: 13, textAlign: 'center' }}>
                  No conditions yet. Click "Add" to add one.
                </div>
              )}
              {form.conditii.map((c, idx) => {
                const tipObj = TIPURI.find(t => t.value === c.tip) || {};
                return (
                  <div key={idx} className="condition-row">
                    <select value={c.tip} onChange={e => modificaConditie(idx, 'tip', e.target.value)} className="condition-select" style={{ flex: 2 }}>
                      {TIPURI.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    <select value={c.operator} onChange={e => modificaConditie(idx, 'operator', e.target.value)} className="condition-select">
                      {OPERATORI.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <input
                      type="number"
                      value={c.valoare}
                      onChange={e => modificaConditie(idx, 'valoare', e.target.value)}
                      className="condition-input"
                      placeholder={tipObj.unit || 'value'}
                    />
                    <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600, minWidth: 30 }}>{tipObj.unit || ''}</span>
                    <button type="button" onClick={() => stergeConditie(idx)} className="btn btn-danger btn-sm">
                      <FaTrash style={{ fontSize: 10 }} />
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Time + type row */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>Confirmation time</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="number"
                    min="0"
                    placeholder="0"
                    value={form.timp.value}
                    onChange={e => setForm({ ...form, timp: { ...form.timp, value: e.target.value } })}
                    className="modal-input"
                    style={{ flex: 1 }}
                  />
                  <select
                    value={form.timp.unit}
                    onChange={e => setForm({ ...form, timp: { ...form.timp, unit: e.target.value } })}
                    className="modal-select"
                    style={{ flex: 1 }}
                  >
                    <option value="seconds">seconds</option>
                    <option value="minutes">minutes</option>
                  </select>
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>Alert type</label>
                <select
                  value={form.tip}
                  onChange={e => setForm({ ...form, tip: e.target.value })}
                  className="modal-select"
                >
                  <option value="">Choose alert type</option>
                  {TIP_ALERTA.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 8, borderTop: '1px solid #e2e8f0' }}>
              <button onClick={() => setShowForm(false)} className="btn btn-ghost btn-md">Cancel</button>
              <button onClick={handleAdd} className="btn btn-success btn-md">Save Scenario</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
